import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { MarketingNav } from "../components/Nav";
import Footer from "../components/Footer";

const principles = [
  {
    icon: "🛡",
    title: "Safety first, always",
    body: "Every feature is reviewed for safety implications before it ships. We have a dedicated trust and safety team who monitors, investigates, and acts — daily.",
  },
  {
    icon: "🔒",
    title: "Privacy by design",
    body: "Your shortlist, your decisions, your conversations — private by default. We share nothing about your journey without your explicit permission.",
  },
  {
    icon: "⚖️",
    title: "Zero tolerance for harm",
    body: "Harassment, threats, impersonation, and abuse will result in immediate account removal. No second chances for serious violations.",
  },
  {
    icon: "💬",
    title: "Consent in every interaction",
    body: "Messaging only opens when both people have matched. No one can contact you without your prior expression of interest.",
  },
];

const standards = [
  {
    title: "Be honest",
    body: "Use real photos. Represent yourself accurately. Misleading profiles undermine the trust of every member — and will be removed.",
  },
  {
    title: "Be respectful",
    body: "Treat every person on the platform with dignity. Rejection is part of the process. How you respond to it defines your character.",
  },
  {
    title: "Be intentional",
    body: "Bora Moyo is for people who are genuinely looking for connection. Using the platform for commercial promotion, hookup culture, or social media following-building is not appropriate here.",
  },
  {
    title: "Be patient",
    body: "Real connections take time. The round structure is designed to be unhurried. Trust the process.",
  },
  {
    title: "Protect privacy",
    body: "Don't share screenshots of private conversations. Don't reveal identifying information about your matches to others without their consent.",
  },
  {
    title: "Report, don't retaliate",
    body: "If someone makes you uncomfortable, use the report feature. Do not attempt to confront or retaliate. We will handle it.",
  },
];

const reportItems = [
  "Harassment or threatening messages",
  "Fake or impersonation profiles",
  "Solicitation or commercial spam",
  "Inappropriate or non-consensual content",
  "Underage users",
  "Any behaviour that makes you feel unsafe",
];

export default function Safety() {
  useEffect(() => {
    document.title = "Safety & Community Guidelines — Bora Moyo";
  }, []);

  return (
    <div className="bg-[#05070d] min-h-screen text-white">
      <MarketingNav />

      <section className="pt-40 pb-20 md:pt-52 md:pb-28 px-6 md:px-12">
        <div className="mx-auto max-w-4xl">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-5">Trust &amp; Safety</div>
          <h1 className="font-display text-5xl md:text-7xl leading-[1.02] tracking-tight mb-8">
            Safety &amp;<br />
            <span className="italic text-[#D4AF37]">Community</span><br />
            Guidelines
          </h1>
          <p className="text-white/60 text-xl max-w-2xl leading-relaxed">
            Bora Moyo is built on the belief that the best connections happen in safe, intentional spaces. These guidelines exist to protect that space for everyone.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-6 md:px-12"><div className="h-px bg-white/5" /></div>

      {/* Core safety principles */}
      <section className="py-20 px-6 md:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">Our commitments</div>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-14">How we keep you safe</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {principles.map((p) => (
              <div key={p.title} className="backdrop-blur-xl bg-[#0C111D]/65 border border-white/8 rounded-2xl p-7">
                <div className="text-3xl mb-5">{p.icon}</div>
                <div className="font-display text-2xl mb-3">{p.title}</div>
                <p className="text-white/55 text-sm leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community standards */}
      <section className="py-20 border-t border-white/5 px-6 md:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">Community standards</div>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-14">How we expect everyone to show up</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {standards.map((s, i) => (
              <div key={s.title} className="backdrop-blur-xl bg-[#0C111D]/65 border border-white/8 rounded-2xl p-7">
                <div className="font-mono text-xs text-[#D4AF37] tracking-[0.2em] mb-4">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-display text-xl mb-3">{s.title}</div>
                <p className="text-white/55 text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Report */}
      <section className="py-20 border-t border-white/5 px-6 md:px-12">
        <div className="mx-auto max-w-6xl grid md:grid-cols-2 gap-16 items-start">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">Reporting</div>
            <h2 className="font-display text-4xl leading-[1.05] mb-6">When to report</h2>
            <p className="text-white/60 text-sm leading-relaxed mb-8">
              Use the in-app report button on any profile or message, or contact our safety team directly. Reports are confidential and investigated within 24 hours.
            </p>
            <ul className="space-y-3">
              {reportItems.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-white/70">
                  <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-[#D4AF37] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="backdrop-blur-xl bg-[#0C111D]/65 border border-[rgba(138,33,50,0.3)] rounded-2xl p-8">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">Emergency contacts</div>
            <div className="space-y-5">
              <div>
                <div className="font-display text-lg mb-1">Safety team</div>
                <a href="mailto:safety@boramoyo.com" className="text-[#D4AF37] hover:text-[#F2DE94] transition-colors text-sm">safety@boramoyo.com</a>
                <p className="text-white/45 text-xs mt-1">24-hour response for urgent matters</p>
              </div>
              <div className="h-px bg-white/5" />
              <div>
                <div className="font-display text-lg mb-1">General support</div>
                <a href="mailto:support@boramoyo.com" className="text-[#D4AF37] hover:text-[#F2DE94] transition-colors text-sm">support@boramoyo.com</a>
                <p className="text-white/45 text-xs mt-1">Next business day for non-urgent matters</p>
              </div>
              <div className="h-px bg-white/5" />
              <p className="text-white/40 text-xs leading-relaxed">
                If you are in immediate danger, contact your local emergency services. Bora Moyo is not an emergency service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-white/5 px-6 md:px-12 text-center">
        <div className="mx-auto max-w-xl">
          <div className="font-display text-3xl md:text-4xl leading-tight mb-4">
            Questions about safety?
          </div>
          <p className="text-white/55 text-sm mb-8">Our trust &amp; safety team is always available.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#080B14] font-semibold px-7 py-3.5 hover:scale-[1.02] transition-transform">
            Contact safety team
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d="M5 12h14m-5-5 5 5-5 5"/></svg>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
